import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, tap } from 'rxjs';

import { environment } from '../../../environments/environment';
import { CurrentUser } from '../models/user.model';
import { formatError } from '../utils/format-error.util';

/** Legacy Pass token (password sign-in), kept only while `environment.legacyAuth`. */
const LEGACY_TOKEN_KEY = 'nside_pass_token';

/**
 * Only on localhost: identity-v2 cannot set its `.nside.io` cookie there, so it hands back a one-time `?code=`
 * that is exchanged for a Firebase ID token, which then stands in for the cookie at `get-api-token`.
 */
const DEV_ID_TOKEN_KEY = 'nside_pass_identity_id_token';

/** Re-exchange this long before identity-v2 says the API token expires. */
const TOKEN_EXPIRY_BUFFER_MS = 60_000;

export const GENERIC_FORBIDDEN_MESSAGE = 'Your nSide account cannot use nSide|Pass.';
export const SESSION_REJECTED_MESSAGE =
  'You are signed in to nSide, but nSide|Pass did not accept the session. Try again, or contact support if it keeps happening.';

/** `POST {identityUrl}/api/get-api-token`. Failures come back as HTTP 200 with `success: false`. */
interface ApiTokenResponse {
  success: boolean;
  api_token: string | null;
  uid?: string | null;
  email?: string | null;
  nside_id?: number | null;
  expires_in: number;
  error?: string;
}

interface LegacyTokenResponse {
  access_token: string;
  token_type: string;
}

/**
 * Sign-in is delegated to nSide Identity v2, the way every nSide web app does it. Identity owns the session (an
 * HttpOnly `__nside_id_v2` cookie on `.nside.io`); this service exchanges it for a one-hour RS256 API token,
 * kept in memory only, and the Pass API decides org, role and account status from `GET /auth/me`.
 *
 * A legacy password session (`environment.legacyAuth`) uses the Pass API's own token instead, until cutover.
 */
@Injectable({ providedIn: 'root' })
export class AuthService {
  readonly currentUser = signal<CurrentUser | null>(null);
  /** Signed in to nSide, but the Pass API refused the account (403): shown on /login with a Sign out button. */
  readonly accessError = signal<string | null>(null);
  /** Signed in to nSide, but the Pass API rejected the token (401) or it could not be loaded: shown on /login. */
  readonly sessionError = signal<string | null>(null);

  private apiToken: string | null = null;
  private tokenExpiresAt = 0;
  /** Concurrent callers share one round trip to identity-v2 (it rate limits get-api-token). */
  private pendingToken: Promise<string | null> | null = null;
  /** True once identity-v2 answered `get-api-token` with a token this page load: there is a session to refresh. */
  private identitySession = false;

  private resolveReady!: () => void;
  private readonly readyPromise = new Promise<void>((resolve) => (this.resolveReady = resolve));

  constructor(
    private readonly http: HttpClient,
    private readonly router: Router,
  ) {}

  get isAuthenticated(): boolean {
    return !!this.currentUser();
  }

  /** An identity-v2 session was seen this page load. */
  get hasIdentitySession(): boolean {
    return this.identitySession;
  }

  /** Something to send as a bearer token: an identity session, or a legacy password token. */
  get hasSession(): boolean {
    return this.identitySession || !!this.legacyToken;
  }

  /** The legacy Pass token, or null; always null once `legacyAuth` is off. */
  get legacyToken(): string | null {
    if (!environment.legacyAuth) {
      return null;
    }
    try {
      return localStorage.getItem(LEGACY_TOKEN_KEY);
    } catch {
      return null;
    }
  }

  /** Resolves once `bootstrap()` has finished, whatever the outcome. Guards wait for it. */
  ready(): Promise<void> {
    return this.readyPromise;
  }

  /**
   * Runs once at startup (APP_INITIALIZER): picks up an identity session if there is one (or a legacy token),
   * and loads the Pass account. Never throws; a refused or rejected account ends up in `accessError` /
   * `sessionError` for /login to show.
   */
  async bootstrap(): Promise<void> {
    try {
      await this.consumeAuthCode();
      const token = await this.getValidToken();
      if (!token) {
        return;
      }
      await this.loadCurrentUser();
    } catch {
      // Identity unreachable or similar: start signed out; /login offers the button.
    } finally {
      this.resolveReady();
    }
  }

  /**
   * The bearer token for a Pass API call: the identity API token (re-exchanged when missing or about to
   * expire; `force` re-exchanges now), else the legacy token. Null when there is no session of either kind.
   */
  async getValidToken(force = false): Promise<string | null> {
    if (!force && this.apiToken && Date.now() < this.tokenExpiresAt) {
      return this.apiToken;
    }
    if (!this.pendingToken) {
      this.pendingToken = this.requestApiToken().finally(() => (this.pendingToken = null));
    }
    const token = await this.pendingToken;
    return token ?? this.legacyToken;
  }

  /** Sends the browser to the nSide Identity sign-in page, which returns to `returnUrl` afterwards. */
  login(returnUrl = '/profiles'): void {
    this.sessionError.set(null);
    const redirect = encodeURIComponent(`${window.location.origin}${this.safeReturnUrl(returnUrl)}`);
    this.redirect(`${environment.identityUrl}/login?redirect=${redirect}`);
  }

  /** Legacy password sign-in against the Pass API; only while `environment.legacyAuth`. */
  loginWithPassword(email: string, password: string): Observable<LegacyTokenResponse> {
    const body = new URLSearchParams();
    body.set('username', email);
    body.set('password', password);
    return this.http
      .post<LegacyTokenResponse>(`${environment.apiUrl}/auth/login`, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
      .pipe(
        tap((res) => {
          try {
            localStorage.setItem(LEGACY_TOKEN_KEY, res.access_token);
          } catch {
            // localStorage unavailable; the token lasts for this page load only
          }
        }),
      );
  }

  /** Loads the Pass account for the current session; sets `currentUser`, or `accessError` / `sessionError`. */
  fetchCurrentUser(): Observable<CurrentUser> {
    return this.http.get<CurrentUser>(`${environment.apiUrl}/auth/me`).pipe(
      tap((user) => {
        this.currentUser.set(user);
        this.accessError.set(null);
        this.sessionError.set(null);
      }),
    );
  }

  /**
   * Ends the session. With an identity session the browser goes to identity's logout page (which revokes the
   * cookie) and comes back to the home page; a legacy-only session just returns to /login.
   */
  logout(): void {
    const hadIdentitySession = this.identitySession;
    this.clearSession();
    if (hadIdentitySession) {
      const redirect = encodeURIComponent(`${window.location.origin}/`);
      this.redirect(`${environment.identityUrl}/logout?redirect=${redirect}`);
      return;
    }
    void this.router.navigate(['/login']);
  }

  /** Forgets everything held here. Does not touch identity's cookie; `logout()` does. */
  clearSession(): void {
    this.apiToken = null;
    this.tokenExpiresAt = 0;
    this.identitySession = false;
    this.currentUser.set(null);
    this.accessError.set(null);
    try {
      localStorage.removeItem(LEGACY_TOKEN_KEY);
      sessionStorage.removeItem(DEV_ID_TOKEN_KEY);
    } catch {
      // ignore
    }
  }

  /** Only same-app paths, never an absolute or protocol-relative URL (open-redirect guard). */
  safeReturnUrl(url: string | null | undefined, fallback = '/profiles'): string {
    return url && url.startsWith('/') && !url.startsWith('//') ? url : fallback;
  }

  /** Full-page navigation. A method so tests can replace it; Karma must never leave its own page. */
  redirect(url: string): void {
    window.location.href = url;
  }

  /** The current page URL. A method so tests can feed a `?code=`. */
  currentUrl(): string {
    return window.location.href;
  }

  /** Rewrites the address bar without navigating (used to drop `?code=`). A method so tests can stub it. */
  replaceUrl(url: string): void {
    history.replaceState(history.state, '', url);
  }

  private async loadCurrentUser(): Promise<void> {
    try {
      await firstValueFrom(this.fetchCurrentUser());
    } catch (err) {
      this.currentUser.set(null);
      if (err instanceof HttpErrorResponse && err.status === 403) {
        // Pass knows who this is and says no (no account, not ACTIVE): keep the session so /login can explain.
        this.accessError.set(formatError(err, GENERIC_FORBIDDEN_MESSAGE));
      } else if (err instanceof HttpErrorResponse && err.status === 401) {
        // The API rejected the token itself (issuer/audience/keys). Drop it so nothing loops back to identity.
        this.sessionError.set(SESSION_REJECTED_MESSAGE);
        this.apiToken = null;
        this.tokenExpiresAt = 0;
        this.identitySession = false;
      } else {
        this.sessionError.set(formatError(err, 'Could not load your nSide|Pass account. Please try again.'));
      }
    }
  }

  private async requestApiToken(): Promise<string | null> {
    // The HttpOnly identity cookie rides along via withCredentials; on localhost the dev ID token goes instead.
    let body = new HttpParams();
    const idToken = this.readDevIdToken();
    if (idToken) {
      body = body.set('id_token', idToken);
    }
    try {
      const res = await firstValueFrom(
        this.http.post<ApiTokenResponse>(`${environment.identityUrl}/api/get-api-token`, body, { withCredentials: true }),
      );
      if (!res.success || !res.api_token) {
        this.apiToken = null;
        this.identitySession = false;
        return null;
      }
      this.apiToken = res.api_token;
      this.tokenExpiresAt = Date.now() + res.expires_in * 1000 - TOKEN_EXPIRY_BUFFER_MS;
      this.identitySession = true;
      return this.apiToken;
    } catch {
      // Identity unreachable, or its CORS list does not have this origin: no identity session this time.
      return null;
    }
  }

  private async consumeAuthCode(): Promise<void> {
    const url = new URL(this.currentUrl());
    const code = url.searchParams.get('code');
    if (!code) {
      return;
    }
    url.searchParams.delete('code');
    this.replaceUrl(`${url.pathname}${url.search}${url.hash}`);
    try {
      const res = await firstValueFrom(
        this.http.post<{ token: string | null }>(`${environment.identityUrl}/exchange-code`, new HttpParams().set('code', code)),
      );
      if (res.token) {
        sessionStorage.setItem(DEV_ID_TOKEN_KEY, res.token);
      }
    } catch {
      // Expired or already-used code: the person just signs in again.
    }
  }

  private readDevIdToken(): string | null {
    try {
      return sessionStorage.getItem(DEV_ID_TOKEN_KEY);
    } catch {
      return null;
    }
  }
}
